import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermission } from "../../hooks/usePermissions";
import styles from "./Notifications.module.css";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import AddNotifications from "./AddNotifications";
import { formatAdminDate, formatIST } from "../../utils/datetime";

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const Notifications = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [notificationsTypeOptions, setNotificationsTypeOptions] = useState([]);
  const [notificationtype, setNotificationType] = useState();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const limit = 20;

  const { hasPermission } = usePermission();

  const fetchNotifications = async (search = false) => {
    setLoading(true);
    try {
      const params = {
        limit,
        page,
      };

      if (search) {
        if (notificationtype) params.notificationtype = notificationtype;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
      }

      const response = await api.get("/notifications", { params });
      setNotifications(response.data || []);
      setTotalPages(response.pagination.totalPages || 1);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  useEffect(() => {
    const fetchDropDown = async () => {
      try {
        const res = await api.get("/notifications/options");
        if (res.status) {
          setNotificationsTypeOptions(res.data);
        }
      } catch (error) {
        console.error("Error fetching options");
      }
    };
    fetchDropDown();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Notification?"))
      return;

    try {
      await api.delete(`/notifications/${id}`);
      toast.success("Notification deleted successfully");
      fetchNotifications();
    } catch (error) {
      const msg = error?.message || "Cannot delete: This notification is linked.";
      toast.error(msg);
    }
  };

  const handleSendNotification = async (id, title, message, query, notificationtypeid, newsId, stockToken, stockSymbol, stockIsin) => {
    try {
      setLoading(true);
      const payload = {
        id,
        title,
        message,
        query,
        notificationtypeid,
        ...(newsId ? { newsId } : {}),
        ...(stockToken ? { stockToken } : {}),
        ...(stockSymbol ? { stockSymbol } : {}),
        ...(stockIsin ? { stockIsin } : {})
      };
      const response = await api.post("/notification/send", payload);
      toast.success(response.data?.message || `Notification sent successfully to ${response.sent} users!`);
      await fetchNotifications();
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-right" autoClose={2000} />

      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Notifications</h1>
          <p className={styles.pageSubtitle}>Manage and dispatch broadcast messages</p>
        </div>

        {hasPermission("CREATE_NOTIFICATIONS") && (
          <button
            className={styles.addBtn}
            onClick={() => {
              setSelectedNotificationId(null);
              setIsModalOpen(true);
            }}
          >
            + Add Notification
          </button>
        )}
      </div>

      <div className={styles.filtersBar}>
        <div className={styles.filterGroup}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Type</label>
            <select
              className={styles.select}
              value={notificationtype || ""}
              onChange={(e) => setNotificationType(e.target.value)}
            >
              <option value="">All Types</option>
              {notificationsTypeOptions.map((module) => (
                <option key={module.notificationtypeid} value={module.notificationtypeid}>
                  {module.notificationtypename}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>From</label>
            <input
              type="date"
              className={styles.dateInput}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>To</label>
            <input
              type="date"
              className={styles.dateInput}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <button
            className={`${styles.searchBtn} ${styles.mtAuto}`}
            onClick={() => { setPage(1); fetchNotifications(true); }}
          >
            Search
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.w60}>ID</th>
              <th>Title</th>
              <th>Message</th>
              <th>Query Type</th>
              <th>Type</th>
              <th>Sent On</th>
              <th className={styles.w160}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && notifications.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyState}>Loading notifications...</td>
              </tr>
            ) : notifications.length > 0 ? (
              notifications.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className={styles.fontMedium}>{row.title}</td>
                  <td className={styles.maxW250}>{row.message}</td>
                  <td>{row.query_title}</td>
                  <td>{row.notificationtypename}</td>
                  <td>
                    <div>{formatAdminDate(row.sent_time, "—")}</div>
                    <div className={styles.timeText}>{formatIST(row.sent_time)?.clock || '—'}</div>
                  </td>
                  <td>
                    <div className={styles.actionsCell}>
                      <button
                        className={`${styles.iconBtn} ${styles.send}`}
                        onClick={() => handleSendNotification(row.id, row.title, row.message, row.query, row.notificationtypeid, row.news_id, row.stock_token, row.stock_symbol, row.stock_isin)}
                        title={row.is_sent === 1 ? "Sent" : "Send"}
                      >
                        <SendIcon />
                      </button>

                      {hasPermission("MODIFY_NOTIFICATIONS") && (
                        <button
                          className={`${styles.iconBtn} ${styles.edit}`}
                          onClick={() => {
                            setSelectedNotificationId(row.id);
                            setIsModalOpen(true);
                          }}
                          title="Edit"
                        >
                          <EditIcon />
                        </button>
                      )}

                      {hasPermission("DELETE_NOTIFICATIONS") && (
                        <button
                          className={`${styles.iconBtn} ${styles.delete}`}
                          onClick={() => handleDelete(row.id)}
                          title="Delete"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className={styles.emptyState}>No notifications found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Showing page {page} of {totalPages}
          </div>
          <div className={styles.paginationControls}>
            <button
              className={styles.pageBtn}
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              &lt;
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
              if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                return (
                  <button
                    key={p}
                    className={`${styles.pageBtn} ${page === p ? styles.active : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              }
              if (p === page - 2 || p === page + 2) {
                return <span key={p} className={styles.paginationEllipsis}>...</span>;
              }
              return null;
            })}

            <button
              className={styles.pageBtn}
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <FullscreenModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedNotificationId ? "Edit Notification" : "Create Notification"}
          maxWidth="800px"
          disableBackdropClick={true}
        >
          <AddNotifications
            notificationId={selectedNotificationId}
            onSubmitted={() => {
              setIsModalOpen(false);
              fetchNotifications();
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        </FullscreenModal>
      )}
    </div>
  );
};

export default Notifications;
